import { ApiSalesDashboardService } from 'services';
import { getAuthorizationDetails } from 'features/authorization/services';
import { getLockedCrossOperatorForAuthorization } from 'features/authorization/helpers/crossOperatorLock';
import { BASE_RULES_MAX_LIMIT } from 'features/authorization/merchantRules/services';
import { CrossStateEnum } from 'features/authorization/types';
import { OrderTypeEnum } from 'features/common/types';
import { getCoinsByOrderType, postFormData } from 'features/payment/services';
import { IFilledOrderForm, OrderCreatedResult, OrderPaymentConcept } from 'features/payment/types';
import { getSalesOrderList } from 'features/salesOrder/services';
import { ISaleOrderListItem } from 'features/salesOrder/types';
import { fetchPaymentContactData } from 'features/transaction-details/helpers/fetchPaymentContactData';
import { getCardInfo, getOperators } from 'features/transactions/services';
import { errorStatus, extractCardDeskError, isOutageError } from './helpers/errors';
import type { CoreCross } from './helpers/reservations';
import {
	CardDeskChargeStatus,
	CardDeskSettingKey,
	ICardDeskAuthorizationsData,
	ICardDeskAuthorizationsQuery,
	ICardDeskBin,
	ICardDeskBinPatch,
	ICardDeskBinPayload,
	ICardDeskBinsData,
	ICardDeskBinsQuery,
	ICardDeskBoardData,
	ICardDeskBoardQuery,
	ICardDeskCardBin,
	ICardDeskCardDetailData,
	ICardDeskCardsData,
	ICardDeskCardsQuery,
	ICardDeskCarrierRule,
	ICardDeskCharge,
	ICardDeskChargePayload,
	ICardDeskChargesData,
	ICardDeskChargesQuery,
	ICardDeskCrossabilityData,
	ICardDeskCrossabilityQuery,
	ICardDeskCrossingPlatform,
	ICardDeskCrossingPlatformPayload,
	ICardDeskCrossingPlatformsData,
	ICardDeskDetailQuery,
	ICardDeskEmissionDetailData,
	ICardDeskEmissionLogQuery,
	ICardDeskEmissionNote,
	ICardDeskEmissionNotesData,
	ICardDeskEmissionPause,
	ICardDeskEmissionPausesData,
	ICardDeskEnvelope,
	ICardDeskQueues,
	ICardDeskReservation,
	ICardDeskReservationPatch,
	ICardDeskReservationPayload,
	ICardDeskRulePatch,
	ICardDeskRulePayload,
	ICardDeskRulesData,
	ICardDeskRulesQuery,
	ICardDeskSetting,
	ICardDeskSettingOf,
	ICardDeskSettingsData,
	ICardDeskSettingsValues,
	ICardDeskSuggestData,
	ICardDeskSuggestQuery,
} from './types';

// ─── Cliente de la Support API (módulo card-desk) ────────────────────────────
// Mismo cliente y mismo sobre `{ success, data, metadata }` que creditRules.
// El interceptor de `ApiSalesDashboardService` NO desloguea ante un 401/403:
// cada hook atrapa el error y lo muestra en su sección.

type Params = Record<string, string | number | boolean | undefined>;

/** Saca los parámetros sin valor: la API rechaza con un 400 lo que no corresponde. */
function cleanParams(params?: Params): Params | undefined {
	if (!params) return undefined;
	return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ''));
}

function unwrap<T>(envelope: ICardDeskEnvelope<T>, what: string): T {
	if (!envelope?.success) throw new Error(`La Support API respondió sin éxito en ${what}`);
	return envelope.data;
}

async function deskGet<T>(path: string, params?: Params): Promise<T> {
	const res = await ApiSalesDashboardService.get<ICardDeskEnvelope<T>>(path, { params: cleanParams(params) });
	return unwrap(res.data, `GET ${path}`);
}

/** `params` va en la URL (`?agencyId=`); sin él, el pedido sale como siempre. */
async function deskPost<T>(path: string, body: unknown, params?: Params): Promise<T> {
	const res = params
		? await ApiSalesDashboardService.post<ICardDeskEnvelope<T>>(path, body, { params: cleanParams(params) })
		: await ApiSalesDashboardService.post<ICardDeskEnvelope<T>>(path, body);
	return unwrap(res.data, `POST ${path}`);
}

async function deskPatch<T>(path: string, body: unknown): Promise<T> {
	const res = await ApiSalesDashboardService.patch<ICardDeskEnvelope<T>>(path, body);
	return unwrap(res.data, `PATCH ${path}`);
}

async function deskPut<T>(path: string, body: unknown): Promise<T> {
	const res = await ApiSalesDashboardService.put<ICardDeskEnvelope<T>>(path, body);
	return unwrap(res.data, `PUT ${path}`);
}

async function deskDelete<T>(path: string): Promise<T> {
	const res = await ApiSalesDashboardService.delete<ICardDeskEnvelope<T>>(path);
	return unwrap(res.data, `DELETE ${path}`);
}

/** Base del módulo: el cliente ya apunta a `/api`. */
export const CARD_DESK_BASE = 'data/card-desk';

// ─── Reglas por aerolínea ────────────────────────────────────────────────────

/** Máximo de `limit` que acepta `GET /rules` (100 por defecto): el mismo que pide el modal de autorización. */
export const CARD_DESK_RULES_MAX_LIMIT = BASE_RULES_MAX_LIMIT;

export const getCardDeskRules = (query: ICardDeskRulesQuery = {}): Promise<ICardDeskRulesData> =>
	deskGet<ICardDeskRulesData>(`${CARD_DESK_BASE}/rules`, { ...query });

export const postCardDeskRule = (body: ICardDeskRulePayload): Promise<ICardDeskCarrierRule> =>
	deskPost<ICardDeskCarrierRule>(`${CARD_DESK_BASE}/rules`, body);

export const patchCardDeskRule = (id: number, body: ICardDeskRulePatch): Promise<ICardDeskCarrierRule> =>
	deskPatch<ICardDeskCarrierRule>(`${CARD_DESK_BASE}/rules/${encodeURIComponent(String(id))}`, body);

/** Baja lógica: la regla queda con `active=false`. Repetirla no cambia nada. */
export const deleteCardDeskRule = (id: number): Promise<ICardDeskCarrierRule> =>
	deskDelete<ICardDeskCarrierRule>(`${CARD_DESK_BASE}/rules/${encodeURIComponent(String(id))}`);

// ─── Parámetros ──────────────────────────────────────────────────────────────

export const getCardDeskSettings = async (): Promise<ICardDeskSetting[]> => {
	const data = await deskGet<ICardDeskSettingsData>(`${CARD_DESK_BASE}/settings`);
	return data.settings ?? [];
};

/** Cambia un parámetro. El valor va con su tipo JSON (`15`, no `"15"`). */
export const putCardDeskSetting = <K extends CardDeskSettingKey>(
	key: K,
	value: ICardDeskSettingsValues[K],
): Promise<ICardDeskSettingOf<K>> =>
	deskPut<ICardDeskSettingOf<K>>(`${CARD_DESK_BASE}/settings/${encodeURIComponent(key)}`, { value });

// ─── Por dónde se cruza ──────────────────────────────────────────────────────

/** Lo que se ve si la API todavía es anterior a API-9: sin reglas de por dónde se cruza. */
export const CARD_DESK_NO_CROSSING_RULES = 'Esta versión de la API todavía no tiene las reglas de por dónde se cruza.';

/**
 * Lo emitido por aerolínea y plataforma, con su regla. Una API anterior a API-9 no acepta `agencyId` (400 "property
 * agencyId should not exist": rechaza los parámetros que no conoce) y, sin él, manda las rutas medidas y no
 * `carrierPlatforms`. Las dos cosas se dicen igual, en llano: ni el 400 crudo ni una pantalla rota.
 */
export const getCardDeskCrossability = async (
	query: ICardDeskCrossabilityQuery = {},
): Promise<ICardDeskCrossabilityData> => {
	let data: ICardDeskCrossabilityData;
	try {
		data = await deskGet<ICardDeskCrossabilityData>(`${CARD_DESK_BASE}/crossability`, { ...query });
	} catch (err) {
		const before9 = errorStatus(err) === 400 && extractCardDeskError(err).includes('agencyId should not exist');
		throw before9 ? new Error(CARD_DESK_NO_CROSSING_RULES) : err;
	}
	if (!Array.isArray(data?.carrierPlatforms)) throw new Error(CARD_DESK_NO_CROSSING_RULES);
	return data;
};

/** Las reglas aerolínea × plataforma activas de la agencia. */
export const getCardDeskCrossingPlatforms = async (agencyId: number): Promise<ICardDeskCrossingPlatform[]> => {
	const data = await deskGet<ICardDeskCrossingPlatformsData>(`${CARD_DESK_BASE}/crossing-platforms`, { agencyId });
	return data.crossingPlatforms ?? [];
};

/** Alta de una regla. La plataforma va tal cual (`PlatformOfProvider`). Si ya está cargada, 409. */
export const postCardDeskCrossingPlatform = (
	body: ICardDeskCrossingPlatformPayload,
): Promise<ICardDeskCrossingPlatform> =>
	deskPost<ICardDeskCrossingPlatform>(`${CARD_DESK_BASE}/crossing-platforms`, body);

/** Baja lógica: la regla queda con `active=false`. Repetirla no cambia nada. */
export const deleteCardDeskCrossingPlatform = (id: number): Promise<ICardDeskCrossingPlatform> =>
	deskDelete<ICardDeskCrossingPlatform>(`${CARD_DESK_BASE}/crossing-platforms/${encodeURIComponent(String(id))}`);

// ─── Tarjetas con los datos de su orden ──────────────────────────────────────

/** Máximo de `limit` que acepta `GET /cards` (50 por defecto). */
export const CARD_DESK_CARDS_MAX_LIMIT = 200;

/**
 * Tarjetas de la cartera, de la más urgente a la menos. `ids` va como
 * `ids=1,2,3`: axios mandaría `ids[]=1&ids[]=2`, que la API no reconoce.
 */
export const getCardDeskCards = (query: ICardDeskCardsQuery = {}): Promise<ICardDeskCardsData> => {
	const { ids, ...rest } = query;
	return deskGet<ICardDeskCardsData>(`${CARD_DESK_BASE}/cards`, {
		...rest,
		ids: ids && ids.length > 0 ? ids.join(',') : undefined,
	});
};

// ─── El tablero ──────────────────────────────────────────────────────────────
// Una API sin reservas (anterior a API-3) no manda `warnings`,
// `reconciliationFailed`, `totals.reservations` ni `reservations[]`: acá se
// completan con lo que quiere decir que no hay. `totals.expiring` queda sin
// dato (la pantalla dice "—"). Sin `queues` (anterior a API-6) la mesa muestra
// las acciones agrupadas; con ellas, cada cola llega como lista.

/** Una emisión o una tarjeta con sus reservas: sin el campo, ninguna. */
const withReservations = <T extends { reservations?: ICardDeskReservation[] }>(item: T): T => ({
	...item,
	reservations: item.reservations ?? [],
});

/** `limit` por defecto de `GET /board` (pagina las tres listas; acepta hasta 500). */
export const CARD_DESK_BOARD_LIMIT = 200;

/** Las colas, cada una como lista aunque la API no la mande; sin `queues`, nada. */
const withQueues = (queues: Partial<ICardDeskQueues> | undefined): ICardDeskQueues | undefined =>
	queues
		? {
				toIssue: queues.toIssue ?? [],
				toAuthorize: queues.toAuthorize ?? [],
				authorizationAlerts: queues.authorizationAlerts ?? [],
		  }
		: undefined;

/** El tablero de la mesa. La API lo calcula y lo guarda 30 segundos por agencia. */
export const getCardDeskBoard = async (query: ICardDeskBoardQuery = {}): Promise<ICardDeskBoardData> => {
	const board = await deskGet<ICardDeskBoardData>(`${CARD_DESK_BASE}/board`, { ...query });
	return {
		...board,
		queues: withQueues(board.queues),
		reconciliationFailed: board.reconciliationFailed ?? false,
		sevensQuota: board.sevensQuota ?? null,
		warnings: board.warnings ?? [],
		totals: { ...board.totals, reservations: board.totals.reservations ?? { count: 0, amount: 0 } },
		emissions: board.emissions.map(withReservations),
		cards: board.cards.map(withReservations),
	};
};

/** Una tarjeta de la mesa, con las aerolíneas que la admiten. 404 si no está en la mesa. */
export const getCardDeskCardDetail = async (
	transactionId: number,
	query: ICardDeskDetailQuery = {},
): Promise<ICardDeskCardDetailData> => {
	const data = await deskGet<ICardDeskCardDetailData>(
		`${CARD_DESK_BASE}/cards/${encodeURIComponent(String(transactionId))}`,
		{ ...query },
	);
	return { ...data, card: withReservations(data.card) };
};

/** `GET /emissions/:flightServiceId` como lo usa la pantalla. */
export interface ICardDeskEmissionDetail extends ICardDeskEmissionDetailData {
	/**
	 * No viene de la API: la emisión trajo `reservations`. Sin ellas, esa API no
	 * tiene reservas y el panel no ofrece reservar, liberar ni cargar el boleto.
	 */
	reservationsSupported: boolean;
}

/** Una emisión de la mesa, con el cobro de su orden. 404 si no es una emisión viva. */
export const getCardDeskEmissionDetail = async (
	flightServiceId: string,
	query: ICardDeskDetailQuery = {},
): Promise<ICardDeskEmissionDetail> => {
	const data = await deskGet<ICardDeskEmissionDetailData>(
		`${CARD_DESK_BASE}/emissions/${encodeURIComponent(flightServiceId)}`,
		{ ...query },
	);
	return {
		...data,
		emission: withReservations(data.emission),
		reservationsSupported: Array.isArray(data.emission.reservations),
	};
};

// ─── Notas y pausa de una emisión (API-7d) ───────────────────────────────────
// Con el mismo `?agencyId=` que el detalle. Cada escritura borra el tablero guardado de la agencia.

const emissionLogPath = (flightServiceId: string, what: string): string =>
	`${CARD_DESK_BASE}/emissions/${encodeURIComponent(flightServiceId)}/${what}`;

/** Lo más que aceptan las notas y las pausas en `limit`. */
export const CARD_DESK_LOG_MAX_LIMIT = 500;

export const getCardDeskEmissionNotes = (
	flightServiceId: string,
	query: ICardDeskEmissionLogQuery = {},
): Promise<ICardDeskEmissionNotesData> =>
	deskGet<ICardDeskEmissionNotesData>(emissionLogPath(flightServiceId, 'notes'), { ...query });

/** 400 con un texto vacío o de más de 2.000 caracteres; 404 si la emisión ya no está. */
export const postCardDeskEmissionNote = (
	flightServiceId: string,
	text: string,
	query: ICardDeskDetailQuery = {},
): Promise<ICardDeskEmissionNote> =>
	deskPost<ICardDeskEmissionNote>(emissionLogPath(flightServiceId, 'notes'), { text }, { ...query });

/** Pausa (`pause`) o despausa (`resume`): 409 si ya estaba así. */
export const postCardDeskEmissionPause = (
	flightServiceId: string,
	what: 'pause' | 'resume',
	query: ICardDeskDetailQuery = {},
): Promise<ICardDeskEmissionPause> =>
	deskPost<ICardDeskEmissionPause>(emissionLogPath(flightServiceId, what), {}, { ...query });

export const getCardDeskEmissionPauses = (
	flightServiceId: string,
	query: ICardDeskEmissionLogQuery = {},
): Promise<ICardDeskEmissionPausesData> =>
	deskGet<ICardDeskEmissionPausesData>(emissionLogPath(flightServiceId, 'pauses'), { ...query });

// ─── Reservas ────────────────────────────────────────────────────────────────
// Cada escritura borra el tablero guardado de la agencia: después se vuelve a pedir.

const reservationPath = (id: number): string => `${CARD_DESK_BASE}/reservations/${encodeURIComponent(String(id))}`;

/** Reserva parte de una autorización para una emisión. */
export const postCardDeskReservation = (body: ICardDeskReservationPayload): Promise<ICardDeskReservation> =>
	deskPost<ICardDeskReservation>(`${CARD_DESK_BASE}/reservations`, body);

/** Cambia el monto (o la nota) de una vigente. */
export const patchCardDeskReservation = (id: number, body: ICardDeskReservationPatch): Promise<ICardDeskReservation> =>
	deskPatch<ICardDeskReservation>(reservationPath(id), body);

/** La libera (`released`): su saldo vuelve a quedar libre. No se borra. */
export const deleteCardDeskReservation = (id: number): Promise<ICardDeskReservation> =>
	deskDelete<ICardDeskReservation>(reservationPath(id));

/** Declara los cruces de core hechos para la reserva con el boleto todavía sin cargar. Repetido, igual. */
export const postCardDeskReservationCrosses = (id: number, crossIds: number[]): Promise<ICardDeskReservation> =>
	deskPost<ICardDeskReservation>(`${reservationPath(id)}/crosses`, { crossIds });

/** Máximo de `limit` que acepta `GET /authorizations` (20 por defecto): lo que pide la lista de la Mesa. */
export const CARD_DESK_AUTHORIZATIONS_MAX_LIMIT = 100;

/** Las autorizaciones con saldo y no vencidas (de una aerolínea, para reservar, o todas). Solo lectura. */
export const getCardDeskAuthorizations = (query: ICardDeskAuthorizationsQuery): Promise<ICardDeskAuthorizationsData> =>
	deskGet<ICardDeskAuthorizationsData>(`${CARD_DESK_BASE}/authorizations`, { ...query });

// ─── El banco de las tarjetas en cartera ("Detectar bancos") ─────────────────
// Core guarda el número cifrado y la mesa solo ve los últimos 4. El operador
// puede ver la tarjeta (el mismo servicio que el detalle de transacción): de
// ahí salen los primeros 8 dígitos y nada más. El número y el código no salen
// de `readCardBin`: no van a estado, almacenamiento, URL, DOM ni logs.

/** Los primeros 8 dígitos de la tarjeta, leídos de core; `null` si el usuario no puede ver el número. */
async function readCardBin(transactionId: number): Promise<string | null> {
	const { data } = await getCardInfo(transactionId);
	if (data?.flagShowCardInfo?.showCreditCardNumber !== true) return null;
	const bin = String(data.cardNumber ?? '')
		.replace(/\D/g, '')
		.slice(0, 8);
	return bin.length === 8 ? bin : null;
}

/** Guarda el BIN de una tarjeta de la cartera: a la Support API viaja solo `{ bin }` (6 a 8 dígitos). */
export const postCardDeskCardBin = (transactionId: number, bin: string): Promise<ICardDeskCardBin> =>
	deskPost<ICardDeskCardBin>(`${CARD_DESK_BASE}/cards/${encodeURIComponent(String(transactionId))}/bin`, { bin });

/**
 * Lo que pasó con una tarjeta: se salteó (no se puede ver su número), la Support API no guardó su BIN (`saved: false`;
 * `rejected` si lo rechazó, un 4xx de esa tarjeta como un sello distinto entre el catálogo y core, y si no, por la
 * red, un 5xx o la sesión vencida) o quedó su banco (`null` si no se conoce). Si falla core, la promesa se rechaza.
 */
export type CardBankDetection =
	| { skipped: true }
	| { skipped: false; saved: false; rejected: boolean }
	| { skipped: false; saved: true; bank: string | null };

/** Detecta el banco de una tarjeta de la cartera: pide la tarjeta a core y manda solo su BIN. */
export async function detectCardBank(transactionId: number): Promise<CardBankDetection> {
	const bin = await readCardBin(transactionId);
	if (bin === null) return { skipped: true };
	try {
		const saved = await postCardDeskCardBin(transactionId, bin);
		return { skipped: false, saved: true, bank: saved?.bank ?? null };
	} catch (err) {
		// Del error, solo si es de esta tarjeta: no sale de acá (lleva el pedido, y el pedido lleva el BIN).
		return { skipped: false, saved: false, rejected: !isOutageError(err) };
	}
}

// ─── El cruce, con el modal de core ──────────────────────────────────────────

/** Lo que `CreateETicketModal` pide de una autorización, además de su id. */
export interface ICardDeskCrossContext {
	operators: { value: string; description: string }[];
	/** El operador de los cruces que ya tiene: el resto va con el mismo. */
	lockedOperatorCode: string | null;
	pendingToCross: number;
	/**
	 * Le queda saldo para cruzar: la guarda `canCross` del detalle de transacción
	 * (`computeAuthorizationBalances`), saldo vigente y sin cruzar mayores que 0.
	 */
	canCross: boolean;
	coinCode: string;
	authorizationCreatedAt: string;
	chargedAgainstOptionName: string;
	/** Los cruces que ya tenía antes de abrir el modal: los nuevos son los que se declaran en la reserva. */
	crossIds: number[];
}

/** La autorización y los operadores, leídos de core como los lee el detalle de una autorización. */
export async function getCardDeskCrossContext(authorizationId: number): Promise<ICardDeskCrossContext> {
	const [details, operators] = await Promise.all([getAuthorizationDetails(authorizationId), getOperators()]);
	const { authorizationData, crossesData } = details.data;
	const pendingToCross = parseFloat(String(authorizationData.pendingToCross ?? 0)) || 0;
	const currentBalance = Math.max(0, Number(authorizationData.amountAuthorized) || 0);
	return {
		crossIds: (crossesData ?? []).map((cross) => cross.id),
		operators: operators.data.map(({ value, description }) => ({ value, description })),
		lockedOperatorCode: getLockedCrossOperatorForAuthorization(crossesData ?? [])?.operatorCode ?? null,
		pendingToCross,
		canCross: currentBalance > 0 && pendingToCross > 0,
		coinCode: authorizationData.coinCode || 'USD',
		authorizationCreatedAt: authorizationData.createdAt ?? '',
		chargedAgainstOptionName: authorizationData.chargedAgainstOptionName ?? '',
	};
}

/** Los cruces vigentes (`CROSS`) de la autorización en core, leídos otra vez después del modal, con quién los cargó. */
export async function getCardDeskAuthorizationCrosses(authorizationId: number): Promise<CoreCross[]> {
	const { data } = await getAuthorizationDetails(authorizationId);
	return (data.crossesData ?? [])
		.filter((cross) => cross.state === CrossStateEnum.CROSS)
		.map(({ id, eticket, amount, coinCode, createdBy }) => ({
			id,
			eticket: eticket ?? '',
			amount: Number(amount) || 0,
			coinCode: coinCode ?? '',
			createdBy: createdBy ?? '',
		}));
}

// ─── Catálogo de BINs ────────────────────────────────────────────────────────

/** Máximo de `limit` que acepta `GET /bins` (hoy hay 17). */
export const CARD_DESK_BINS_MAX_LIMIT = 200;

const binPath = (id: number): string => `${CARD_DESK_BASE}/bins/${encodeURIComponent(String(id))}`;

export const getCardDeskBins = (query: ICardDeskBinsQuery = {}): Promise<ICardDeskBinsData> =>
	deskGet<ICardDeskBinsData>(`${CARD_DESK_BASE}/bins`, { ...query });

export const postCardDeskBin = (body: ICardDeskBinPayload): Promise<ICardDeskBin> =>
	deskPost<ICardDeskBin>(`${CARD_DESK_BASE}/bins`, body);

export const patchCardDeskBin = (id: number, body: ICardDeskBinPatch): Promise<ICardDeskBin> =>
	deskPatch<ICardDeskBin>(binPath(id), body);

/** Baja lógica: queda con `active=false`. */
export const deleteCardDeskBin = (id: number): Promise<ICardDeskBin> => deskDelete<ICardDeskBin>(binPath(id));

// ─── Cobros con link propio ──────────────────────────────────────────────────

/** Máximo de `limit` que acepta `GET /charges`. */
export const CARD_DESK_CHARGES_MAX_LIMIT = 200;

export const getCardDeskCharges = (query: ICardDeskChargesQuery = {}): Promise<ICardDeskChargesData> =>
	deskGet<ICardDeskChargesData>(`${CARD_DESK_BASE}/charges`, { ...query });

/** Los cobros de una sale order (`GET /charges?saleOrderId=`), de esa agencia y, si se pide, de ese estado. */
export async function getCardDeskSaleOrderCharges(
	agencyId: number,
	saleOrderId: string,
	status?: CardDeskChargeStatus,
): Promise<ICardDeskCharge[]> {
	const data = await getCardDeskCharges({ agencyId, saleOrderId, status, limit: CARD_DESK_CHARGES_MAX_LIMIT });
	return data.charges ?? [];
}

/** Registra el cobro de un link de core ya creado. Devuelve el token del cliente. */
export const postCardDeskCharge = (body: ICardDeskChargePayload): Promise<ICardDeskCharge> =>
	deskPost<ICardDeskCharge>(`${CARD_DESK_BASE}/charges`, body);

/** Lo cancela: el token deja de servir. El link de core no se toca. */
export const deleteCardDeskCharge = (id: number): Promise<ICardDeskCharge> =>
	deskDelete<ICardDeskCharge>(`${CARD_DESK_BASE}/charges/${encodeURIComponent(String(id))}`);

/** El plan de cobro sugerido para la orden y la tarjeta del cliente. */
export const getCardDeskSuggest = (query: ICardDeskSuggestQuery): Promise<ICardDeskSuggestData> =>
	deskGet<ICardDeskSuggestData>(`${CARD_DESK_BASE}/suggest`, { ...query });

/** La sale order con ese número, con el servicio del listado de órdenes (la agencia de la sesión). */
export async function findCardDeskSaleOrder(orderNumber: number): Promise<ISaleOrderListItem | null> {
	const { data } = await getSalesOrderList(1, 1, [], { orderNumber });
	return (data.elements ?? []).find((order) => order.orderNumber === orderNumber) ?? null;
}

/** Lo que pide el link de core además de lo que sale de la orden y del caso. */
export interface ICardDeskCoreLinkInput {
	order: Pick<ISaleOrderListItem, 'dealId' | 'isBitrixDeal' | 'currency'>;
	paymentConcept: OrderPaymentConcept;
	amount: number;
	/** Las cuotas; `undefined` donde el formulario de siempre no las manda (Paraguay y Colombia). */
	fee: number[] | undefined;
	plCountryCode: string;
}

/**
 * Crea el link de pago C2A en core con `postFormData`, el servicio de siempre, y el cuerpo que arma el
 * formulario "Nueva Orden de Pago": el contacto del caso (como "Cobrar con DLocal") y la moneda de la orden.
 */
export async function createCardDeskCoreLink(input: ICardDeskCoreLinkInput): Promise<OrderCreatedResult> {
	const { order } = input;
	const [contact, coins] = await Promise.all([
		fetchPaymentContactData(order.dealId, order.isBitrixDeal),
		getCoinsByOrderType(OrderTypeEnum.SIMPLE),
	]);
	const coin = coins.data.find((option) => option.code.toUpperCase() === order.currency?.toUpperCase());
	if (!coin) throw new Error(`Core no tiene la moneda ${order.currency} para los links de pago con tarjeta.`);
	const form: IFilledOrderForm = {
		orderType: OrderTypeEnum.SIMPLE,
		crmTicket: order.dealId,
		isBitrixDeal: order.isBitrixDeal,
		paymentConcept: input.paymentConcept,
		name: contact.name,
		email: contact.email,
		phone: contact.phone,
		fee: input.fee,
		coinId: coin.id,
		amount: input.amount,
		plCountryCode: input.plCountryCode,
	};
	const { data } = await postFormData(form);
	return data;
}
